import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  ArrowUpFromLine,
  Banknote,
  ChevronDown,
  Coins,
  FileText,
  Fuel,
  LayoutDashboard,
  Link2,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Percent,
  Search,
  ShieldAlert,
  SlidersHorizontal,
  Store,
  Undo2,
  Users,
  Webhook,
  X,
  Zap,
  type LucideIcon,
  Inbox,
} from "lucide-react";
import { AdminRole, FireblocksEventPublishStatus, WebhookDeliveryStatus } from "@cpg/common";
import { logout, me } from "@/api/auth";
import { clearTokens, getToken } from "@/api/client";
import { listFireblocksEvents } from "@/api/fireblocks-events";
import { listWebhookDeliveries } from "@/api/webhook-deliveries";
import { hasRole } from "@/lib/roles";
import { type AdminResponse } from "@/api/types";
import { CommandPalette } from "@/components/command-palette";
import { SettingsPopover } from "@/components/settings-popover";

// `beforeLoad` on this ancestor route runs on EVERY navigation into the
// shell, including a filter/sort/page-only search-param change on an
// already-active child route — unlike `loader`, TanStack Router's
// `beforeLoad` is never gated by `staleTime`, so without a cache here every
// single filter click on every list page would pay for a live `/me`
// round-trip before the navigation (and therefore the URL, and therefore
// every filter <select>'s controlled value) could commit. On a slow
// connection, or when a second click supersedes the first click's still-
// pending navigation, that reads as "I changed the filter and nothing
// happened." Caching the result for a bounded window keeps beforeLoad
// synchronous on the hot path while still catching a role change or
// deactivation applied on the backend within that window — mirrors
// backoffice-api's own "active flag re-checked per request" guarantee
// closely enough without gating routing on it.
let cachedAdmin: AdminResponse | null = null;
let cachedAdminAt = 0;
const ADMIN_CACHE_TTL_MS = 60_000;

export const Route = createFileRoute("/_app")({
  beforeLoad: async () => {
    if (typeof window === "undefined") {
      throw redirect({ to: "/login" });
    }

    const token = getToken();
    if (!token) {
      cachedAdmin = null;
      throw redirect({ to: "/login" });
    }

    const now = Date.now();
    if (cachedAdmin && now - cachedAdminAt < ADMIN_CACHE_TTL_MS) {
      return { admin: cachedAdmin };
    }

    try {
      const admin = await me();
      cachedAdmin = admin;
      cachedAdminAt = now;
      return { admin };
    } catch {
      cachedAdmin = null;
      clearTokens();
      throw redirect({ to: "/login" });
    }
  },
  component: AppLayout,
});

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * Roles (besides SUPERADMIN, which always passes) that open this item, or
   * the literal `'any'` meaning "visible regardless of role" — used only by
   * Dashboard, which every authenticated admin lands on and whose own tiles
   * self-gate per-role individually. See `itemVisible` below; `lib/roles.ts`'s
   * `hasRole` itself is unchanged (an empty array there still means
   * "SUPERADMIN only", which is why `'any'` has to be a separate case).
   */
  roles: AdminRole[] | "any";
};

type NavSection = {
  label: string;
  items: NavItem[];
};

const NAV_SECTIONS: NavSection[] = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: "any" },
      {
        to: "/merchants",
        label: "Merchants",
        icon: Store,
        roles: [AdminRole.MERCHANTS_VIEWER, AdminRole.MERCHANTS_MANAGER],
      },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/invoices", label: "Invoices", icon: FileText, roles: [AdminRole.INVOICES_VIEWER] },
      { to: "/refunds", label: "Refunds", icon: Undo2, roles: [AdminRole.INVOICES_VIEWER] },
      {
        to: "/high-risk",
        label: "AML Review",
        icon: ShieldAlert,
        roles: [AdminRole.AML_OFFICER],
      },
      // feature-037: deposits to closed invoices.
      { to: "/unattached-deposits", label: "Unattached", icon: Inbox, roles: [AdminRole.INVOICES_VIEWER] },
    ],
  },
  {
    // Task 14 (feature-031 Phase B): platform-wide channel views under the
    // two new roles. Same lenient VIEWER+MANAGER visibility convention as
    // the Merchants section above (the backend's own GET routes are
    // CHANNELS_VIEWER-only; an admin holding CHANNELS_MANAGER in practice
    // also holds CHANNELS_VIEWER — same non-hierarchical role-pairing
    // assumption Merchants already makes).
    label: "Channels",
    items: [
      {
        to: "/channels",
        label: "Channels",
        icon: Link2,
        roles: [AdminRole.CHANNELS_VIEWER, AdminRole.CHANNELS_MANAGER],
      },
      {
        to: "/channel-payments",
        label: "Payments",
        icon: ArrowLeftRight,
        roles: [AdminRole.CHANNELS_VIEWER, AdminRole.CHANNELS_MANAGER],
      },
      {
        to: "/channel-settlements",
        label: "Settlements",
        icon: Banknote,
        roles: [AdminRole.CHANNELS_VIEWER, AdminRole.CHANNELS_MANAGER],
      },
    ],
  },
  {
    label: "Finance",
    items: [
      {
        to: "/accounts",
        label: "Accounts",
        icon: Banknote,
        roles: [AdminRole.FINANCE_VIEWER],
      },
      {
        to: "/gas-station",
        label: "Gas station",
        icon: Fuel,
        // Both roles, not just FINANCE_VIEWER: roles are non-hierarchical
        // (hasRole requires a literal match), and GET /v1/gas-station itself
        // now accepts CONFIG_MANAGER too — see gas-station.controller.ts's
        // doc comment. Without this a CONFIG_MANAGER-only admin, who can
        // edit the funding floor, would have no nav link to reach it.
        roles: [AdminRole.FINANCE_VIEWER, AdminRole.CONFIG_MANAGER],
      },
      {
        to: "/withdrawals",
        label: "Withdrawals",
        icon: ArrowUpFromLine,
        // Same dual-role visibility as gas-station's entry: GET
        // /v1/withdrawals accepts both FINANCE_VIEWER and CONFIG_MANAGER
        // (Task 6), and only CONFIG_MANAGER can approve/reject.
        roles: [AdminRole.FINANCE_VIEWER, AdminRole.CONFIG_MANAGER],
      },
    ],
  },
  {
    label: "Configuration",
    items: [
      { to: "/assets", label: "Assets", icon: Coins, roles: [AdminRole.CONFIG_MANAGER] },
      { to: "/tariffs", label: "Tariffs", icon: Percent, roles: [AdminRole.CONFIG_MANAGER] },
      {
        to: "/withdrawal-limits",
        label: "Withdraw Limits",
        icon: SlidersHorizontal,
        // Dual-role read, same as its Finance-section siblings (Accounts/
        // Gas station/Withdrawals): GET /v1/withdrawal-limits accepts both
        // FINANCE_VIEWER and CONFIG_MANAGER, only CONFIG_MANAGER can edit —
        // moved here (Configuration) rather than Finance since it's a
        // platform-policy setting, not a balance/transaction view.
        roles: [AdminRole.FINANCE_VIEWER, AdminRole.CONFIG_MANAGER],
      },
    ],
  },
  {
    label: "Technical",
    items: [
      {
        to: "/transactions",
        label: "Transactions",
        icon: ArrowLeftRight,
        roles: [AdminRole.TECH_VIEWER, AdminRole.TECH_MANAGER],
      },
      {
        to: "/webhook-deliveries",
        label: "Webhook deliveries",
        icon: Webhook,
        roles: [AdminRole.TECH_VIEWER, AdminRole.TECH_MANAGER],
      },
      {
        to: "/fireblocks-events",
        label: "Fireblocks events",
        icon: Zap,
        roles: [AdminRole.TECH_VIEWER, AdminRole.TECH_MANAGER],
      },
    ],
  },
  {
    label: "Administration",
    items: [
      {
        // No dedicated viewer role exists for admin management — SUPERADMIN only.
        to: "/admins",
        label: "Admins",
        icon: Users,
        roles: [],
      },
    ],
  },
];

/** `item.roles === 'any'` bypasses `hasRole` entirely — see `NavItem.roles` doc comment. */
function itemVisible(admin: AdminResponse, item: NavItem): boolean {
  return item.roles === "any" || hasRole(admin.roles, ...item.roles);
}

const TITLES: Record<string, { title: string; subtitle: string }> = {
  "/dashboard": {
    title: "Dashboard",
    subtitle: "Payments, finance, health, and merchants overview tiles.",
  },
  "/merchants": { title: "Merchants", subtitle: "Merchant accounts, access, and onboarding." },
  "/invoices": { title: "Invoices", subtitle: "All invoices, filtered by status and merchant." },
  "/refunds": { title: "Refunds", subtitle: "Refund requests across all merchants." },
  "/unattached-deposits": {
    title: "Unattached deposits",
    subtitle: "Deposits sent to an invoice after it closed — credited or refunded separately.",
  },
  "/high-risk": { title: "AML Review", subtitle: "High-risk review queue and case resolution." },
  "/channels": {
    title: "Channels",
    subtitle: "Permanent deposit addresses across every merchant, with the real internal status.",
  },
  "/channel-payments": {
    title: "Payments",
    subtitle: "Every deposit to a channel address, across every merchant.",
  },
  "/channel-settlements": {
    title: "Settlements",
    subtitle: "Channel sweep batches — force one now, or retry a failed one.",
  },
  "/accounts": {
    title: "Accounts",
    subtitle: "Account balances, merchant/project ledger split, and gas spend, across every merchant.",
  },
  "/platform-wallets": {
    title: "Platform wallets",
    subtitle: "Gas station and revenue vault balances.",
  },
  "/gas-station": {
    title: "Gas station",
    subtitle: "Per-asset funding floor and live gas-vault balances.",
  },
  "/withdrawals": {
    title: "Withdrawals",
    subtitle: "Merchant withdrawal requests across every merchant — review, approve, and reject.",
  },
  "/withdrawal-limits": {
    title: "Withdraw Limits",
    subtitle: "Minimum, per-transaction, and rolling 24-hour caps on what a merchant can withdraw.",
  },
  "/assets": {
    title: "Assets & Rates",
    subtitle: "Supported assets, live rates, and enable/disable toggles.",
  },
  "/tariffs": { title: "Tariffs", subtitle: "Fee tariffs and per-asset rules." },
  "/transactions": {
    title: "Transactions",
    subtitle: "All on-chain transactions, with RBF chain detail.",
  },
  "/webhook-deliveries": {
    title: "Webhook deliveries",
    subtitle: "Merchant webhook delivery attempts and status.",
  },
  "/fireblocks-events": { title: "Fireblocks events", subtitle: "Raw custody provider events." },
  "/unmatched-deposits": {
    title: "Unmatched deposits",
    subtitle: "Deposits with no matching invoice.",
  },
  "/admins": { title: "Admins", subtitle: "Backoffice admin accounts, roles, and access." },
  "/profile": { title: "Profile", subtitle: "Your account and security settings." },
};

function metaFor(pathname: string) {
  if (TITLES[pathname]) return TITLES[pathname];
  if (pathname.startsWith("/merchants/")) {
    return { title: "Merchant details", subtitle: "" };
  }
  if (pathname.startsWith("/invoices/")) {
    return { title: "Invoice details", subtitle: "" };
  }
  if (pathname.startsWith("/tariffs/")) {
    return { title: "Tariff rules", subtitle: "" };
  }
  if (pathname.startsWith("/channels/")) {
    return { title: "Channel details", subtitle: "Deposit address, payments, and settlements for this channel." };
  }
  if (pathname.startsWith("/unattached-deposits/")) {
    return { title: "Unattached deposit", subtitle: "The deposit, its original invoice, the decision, refunds and transactions." };
  }
  if (pathname.startsWith("/channel-payments/")) {
    return { title: "Payment details", subtitle: "One channel payment's amounts, and its deposit/settlement transaction history." };
  }
  if (pathname.startsWith("/channel-settlements/")) {
    return { title: "Settlement details", subtitle: "One sweep batch's amounts, and its settlement transaction history." };
  }
  return { title: "", subtitle: "" };
}

function initialsOf(displayName: string): string {
  return (
    displayName
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

/** feature-041: red count pinned to a nav icon's top-right for items stuck for good; hidden at zero. */
function AlertBadge({ count }: { count: number | undefined }) {
  if (!count) return null;
  return (
    <span className="absolute -right-2 -top-1.5 min-w-4 rounded-full bg-destructive px-1 text-center text-[10px] font-semibold leading-4 text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
}

/** feature-041: counts of webhooks / Fireblocks events stuck for good, polled for technical admins only. */
function useStuckCounts(admin: AdminResponse): Record<string, number | undefined> {
  const enabled = hasRole(admin.roles, AdminRole.TECH_VIEWER, AdminRole.TECH_MANAGER);
  const { data: exhausted } = useQuery({
    queryKey: ["webhook-deliveries-exhausted"],
    queryFn: () => listWebhookDeliveries({ status: WebhookDeliveryStatus.EXHAUSTED, pageSize: 1 }),
    refetchInterval: 60_000,
    enabled,
  });
  const { data: failed } = useQuery({
    queryKey: ["fireblocks-events-failed"],
    queryFn: () => listFireblocksEvents({ publishStatus: FireblocksEventPublishStatus.FAILED, pageSize: 1 }),
    refetchInterval: 60_000,
    enabled,
  });
  return {
    "/webhook-deliveries": exhausted?.total,
    "/fireblocks-events": failed?.total,
  };
}

function SidebarNav({
  admin,
  onNavigate,
  collapsed,
  onToggleCollapsed,
}: {
  admin: AdminResponse;
  onNavigate?: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const alerts = useStuckCounts(admin);

  const signOut = () => {
    logout();
    cachedAdmin = null;
    navigate({ to: "/login" });
  };

  const sections = NAV_SECTIONS.filter((section) =>
    section.items.some((item) => itemVisible(admin, item)),
  );
  const width = collapsed ? "w-[68px]" : "w-[220px]";

  return (
    <div className={`flex h-full ${width} flex-col border-r border-[#E5E7EB] bg-white transition-[width] duration-200`}>
      <div className="px-3 pt-4 pb-3 border-b border-[#F1F2F4]">
        {collapsed ? (
          <div className="flex justify-center py-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#0F172A] text-sm font-semibold text-white">
              B
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#0F172A] text-sm font-semibold text-white">
              B
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-[#0F172A] leading-tight">Backoffice</div>
              <div className="truncate text-xs text-[#6B7280]">Workspace</div>
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="space-y-4">
          {sections.map((section) => {
            const visibleItems = section.items.filter((item) => itemVisible(admin, item));
            return (
              <div key={section.label} className="space-y-0.5">
                {!collapsed && (
                  <div className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
                    {section.label}
                  </div>
                )}
                {visibleItems.map((item) => {
                  const active = pathname === item.to || pathname.startsWith(item.to + "/");
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={onNavigate}
                      title={collapsed ? item.label : undefined}
                      className={`group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                        collapsed ? "justify-center" : ""
                      } ${
                        active
                          ? "bg-[#F1F5F9] text-[#0F172A] font-medium"
                          : "text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]"
                      }`}
                    >
                      <span className="relative shrink-0">
                        <Icon
                          className={`h-[18px] w-[18px] ${active ? "text-[#0F172A]" : "text-[#94A3B8] group-hover:text-[#0F172A]"}`}
                        />
                        <AlertBadge count={alerts[item.to]} />
                      </span>
                      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </div>
      </nav>

      <div className="border-t border-[#F1F2F4] p-3">
        <div className={`flex items-center gap-2 px-2 py-1.5 ${collapsed ? "flex-col" : ""}`}>
          {!collapsed && (
            // Identity pill doubles as the entry point to the Profile page
            // (account details + 2FA settings) — mirrors apps/merchant's
            // identity-pill-as-entry-point pattern for its Business page.
            <Link
              to="/profile"
              onClick={onNavigate}
              className="flex flex-1 min-w-0 items-center gap-2 rounded-md py-0.5 hover:opacity-80"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0F172A]">
                <span className="text-[10px] font-bold text-white">{initialsOf(admin.displayName)}</span>
              </div>
              <span className="flex-1 truncate text-sm font-semibold text-[#0F172A]">{admin.displayName}</span>
            </Link>
          )}
          <button
            onClick={onToggleCollapsed}
            className="rounded-md p-1 text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#0F172A]"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
          <button
            onClick={signOut}
            className="rounded-md p-1 text-[#9CA3AF] hover:bg-[#FEE2E2] hover:text-[#DC2626]"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AppLayout() {
  const { admin } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const meta = metaFor(pathname);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("cpg_backoffice_sidebar") === "collapsed";
  });
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    if (meta.title) document.title = `${meta.title} | Backoffice`;
  }, [meta.title]);

  // Ctrl+K / Cmd+K opens the global search command palette from anywhere in
  // the shell (Task 23).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      try {
        window.localStorage.setItem("cpg_backoffice_sidebar", next ? "collapsed" : "expanded");
      } catch {
        // localStorage unavailable (e.g. private browsing) — collapse state
        // just won't persist across reloads.
      }
      return next;
    });
  };

  return (
    <div className="flex min-h-screen w-full bg-[#F8FAFC]">
      <aside className="hidden md:block">
        <SidebarNav admin={admin} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 h-full">
            <SidebarNav
              admin={admin}
              collapsed={false}
              onToggleCollapsed={() => {}}
              onNavigate={() => setDrawerOpen(false)}
            />
          </div>
          <button onClick={() => setDrawerOpen(false)} className="absolute right-4 top-4 rounded-md bg-white p-2 text-[#0F172A] shadow">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-4 border-b border-[#E5E7EB] bg-white px-6 md:px-8 py-5">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setDrawerOpen(true)} className="rounded-md p-1.5 text-[#0F172A] hover:bg-[#F3F4F6] md:hidden">
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-[22px] font-semibold tracking-tight text-[#0F172A] leading-tight">{meta.title}</h1>
              {meta.subtitle && <p className="mt-0.5 text-sm text-[#6B7280] truncate">{meta.subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="hidden sm:flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3 py-1.5 text-xs text-[#6B7280] bg-white hover:bg-[#F8FAFC] hover:text-[#0F172A]"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="font-medium">Ctrl+K</span>
            </button>
            <SettingsPopover />
            <Link
              to="/profile"
              className="flex items-center gap-2 rounded-full border border-[#E5E7EB] py-1 pl-1 pr-3 bg-white hover:bg-[#F8FAFC]"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0F172A] text-[11px] font-semibold text-white">
                {initialsOf(admin.displayName)}
              </div>
              <span className="hidden sm:inline text-sm font-medium text-[#0F172A]">{admin.displayName}</span>
              <ChevronDown className="h-3.5 w-3.5 text-[#6B7280]" />
            </Link>
          </div>
        </header>
        <main className="flex-1 p-6 md:p-8">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
